import { createContext, useContext, useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import {
  onAuthStateChanged,
  signInWithPopup,
  signOut as firebaseSignOut,
  deleteUser,
  sendSignInLinkToEmail,
  isSignInWithEmailLink,
  signInWithEmailLink,
  type User,
} from 'firebase/auth';
import { auth, googleProvider, twitterProvider } from '../firebase';
import {
  syncPublicProfile,
  removePublicProfile,
  createInvite,
  redeemInviteCode,
  type PublicProfile,
  type MatchResult,
  type InviteRedeemResult,
  type ProfileVisibilitySettings,
  DEFAULT_VISIBILITY,
} from '../lib/api';
import { clearConsentRecord } from '../components/ConsentNotice';
import { getPortalConfig } from '../lib/api';
import {
  DEFAULT_ROLE_ACTIVITY,
  FOUNDATION_ROLE,
  normalizeRoleActivity,
  ROLE_OPTIONS,
  type RoleActivityMap,
  type RoleName,
} from '../lib/roles';
import { emitGatewayMetrics } from '../lib/gatewayPolicy';
import type { PlatformPolicyConfig } from '../lib/types';

export type { PublicProfile, MatchResult, InviteRedeemResult };

// ── Profile schema (PII-free) ──────────────────────────────────────────────

export type ProfileKind = 'learner' | 'knowledge_provider' | 'both';
export type SessionMode = 'online' | 'offline';
export type GatewayRole = 'shiksharthi' | 'upadhyaya' | 'acharya' | 'guru' | 'raja' | '';
export type GatewayArchetype = 'anveshak' | 'setu' | 'rakshak' | 'nirmata' | '';

export interface FederatedIdentitySnapshot {
  displayName: string;
  email: string;
  phoneNumber: string;
  photoURL: string;
  providerIds: string[];
  lastSyncedAt: string;
}

export interface RoleGatewayState {
  selectedRole: GatewayRole;
  city: string;
  primaryArchetype: GatewayArchetype;
  secondaryArchetype: GatewayArchetype;
  adhikara: {
    hridaya: number | null;
    vak: number | null;
    jnana: number | null;
    karma: number | null;
    phala: number | null;
  };
  acharya: {
    shravana: boolean;
    smarana: boolean;
    samvada: boolean;
    vinaya: boolean;
    seva: boolean;
    niyama: boolean;
    dambhahin: boolean;
  };
  raja: {
    dana: boolean;
    vrata: boolean;
    brahmacharya: boolean;
    brahma_dharanam: boolean;
    dama: boolean;
    prashantata: boolean;
    bhuta_anukampana: boolean;
    anrishansya: boolean;
    parasva_adana_varjanam: boolean;
    vyalika_karanam_tyaga: boolean;
    matapitru_shushrusha: boolean;
    devata_atithi_guru_pujanam: boolean;
    ghrina: boolean;
    nitya_indriya_sanyama: boolean;
  };
  domainPledges: {
    learning: boolean;
    service: boolean;
    finance: boolean;
    family: boolean;
    community: boolean;
  };
  proposal: {
    projectName: string;
    projectType: 'homemaking' | 'cultural_education' | 'enterprise' | 'media' | 'service' | '';
    primaryCategory: string;
    secondaryCategory: string;
    studiedProjects: string[];
    kramaPath: {
      bija: string;
      ankura: string;
      parna: string;
      pushpa: string;
      moksha: string;
    };
    societalValue: string;
    personalCommitment: string;
    totalEstimate: string;
    duration: string;
    rolesNeeded: string;
    draftPlan: string;
    reviewStatus: 'draft' | 'requested' | 'reviewed';
  };
  guruAttestationStatus: 'pending' | 'requested' | 'approved';
  completedAt: string;
  /** Seva Provider activation — client stores intent; backend owns approval status. */
  sevaProvider: {
    /** User has opted in to becoming a provider. */
    enabled: boolean;
    /** Canonical dimension IDs the user wants to provide in (from vedicDimensions). */
    dimensions: string[];
    /** Optional portfolio/demo URL. */
    portfolioUrl: string;
    /** User has asserted they have relevant capability. */
    serviceAssertions: boolean;
    /** Cached from backend only — never authoritative on client. */
    cachedActivationId: string;
    /** Cached from backend only — frontend display hint only. */
    cachedReviewStatus: 'not_requested' | 'pending' | 'approved' | 'rejected';
  };
}

export interface UserProfile {
  /** Firebase UID — pseudonymous random string, not a real identity. */
  uid: string;
  /** User-chosen display name. Need not be (and should not be) a real name. */
  alias: string;
  profileKind: ProfileKind;
  // Simplified roles per requirements (limited to: "Shiksharthi / Learner", "Educator", "Service Provider", "Service Consumer" for basic profile; mapped internally to legacy)
  role?: 'Shiksharthi / Learner' | 'Educator' | 'Service Provider' | 'Service Consumer';
  age: string;
  rating: string;
  learningLevel: 'beginner' | 'intermediate' | 'advanced';
  learningGoals: string;
  nativeLanguage: string;
  preferredScript: 'devanagari' | 'iast' | 'both';
  activeRole: RoleName;
  /** All roles the user holds — always includes FOUNDATION_ROLE ('संस्कृत शिक्षार्थी'). */
  roles: RoleName[];
  roleActivity: RoleActivityMap;
  /** IANA time zone — region-level only, not a precise location. */
  timeZone: string;
  pincode: string;
  // Core personal data (simplified per requirements) — DoB, Pincode (already), Cell/e-Mail
  dob?: string;          // YYYY-MM-DD
  cell?: string;
  emailContact?: string;
  studyAreas: string;
  qualification: string;
  sessionModes: SessionMode[];
  acharyaAcknowledgedAt: string;
  acharyaAcknowledgedVersion: string;
  roleGateway: RoleGatewayState;
  // ── Discovery/collaboration fields ──
  /** Explicit opt-in to appear in the peer discovery pool. */
  isDiscoverable: boolean;
  availability: ('morning' | 'afternoon' | 'evening' | 'weekends')[];
  collaborationMode: 'sync' | 'async' | 'either';
  lookingFor: 'conversation_partner' | 'study_buddy' | 'mentor' | 'student' | 'peer_group';
  federatedIdentity?: FederatedIdentitySnapshot;
  createdAt: string;
  updatedAt: string;
  // ── Privacy / visibility ──
  /** Per-field visibility audience settings. */
  profileVisibility: ProfileVisibilitySettings;
  /** Emails granted access to 'emails'-audience fields. */
  allowedEmails: string[];
  /** Aliases granted access to 'aliases'-audience fields. */
  allowedAliases: string[];
}

/** Stored connection record (non-PII subset, saved to localStorage). */
export interface Connection {
  uid: string;
  alias: string;
  profileKind?: ProfileKind;
  age?: string;
  rating?: string;
  learningLevel: string;
  nativeLanguage: string;
  timeZone: string;
  pincode?: string;
  studyAreas?: string[];
  qualification?: string;
  sessionModes?: string[];
  lookingFor: string;
  connectedAt: string;
}

const DEFAULT_ROLE_GATEWAY: RoleGatewayState = {
  selectedRole: '',
  city: '',
  primaryArchetype: '',
  secondaryArchetype: '',
  adhikara: {
    hridaya: null,
    vak: null,
    jnana: null,
    karma: null,
    phala: null,
  },
  acharya: {
    shravana: false,
    smarana: false,
    samvada: false,
    vinaya: false,
    seva: false,
    niyama: false,
    dambhahin: false,
  },
  raja: {
    dana: false,
    vrata: false,
    brahmacharya: false,
    brahma_dharanam: false,
    dama: false,
    prashantata: false,
    bhuta_anukampana: false,
    anrishansya: false,
    parasva_adana_varjanam: false,
    vyalika_karanam_tyaga: false,
    matapitru_shushrusha: false,
    devata_atithi_guru_pujanam: false,
    ghrina: false,
    nitya_indriya_sanyama: false,
  },
  domainPledges: {
    learning: false,
    service: false,
    finance: false,
    family: false,
    community: false,
  },
  proposal: {
    projectName: '',
    projectType: '',
    primaryCategory: '',
    secondaryCategory: '',
    studiedProjects: [],
    kramaPath: {
      bija: '',
      ankura: '',
      parna: '',
      pushpa: '',
      moksha: '',
    },
    societalValue: '',
    personalCommitment: '',
    totalEstimate: '',
    duration: '',
    rolesNeeded: '',
    draftPlan: '',
    reviewStatus: 'draft',
  },
  guruAttestationStatus: 'pending',
  completedAt: '',
  sevaProvider: {
    enabled: false,
    dimensions: [],
    portfolioUrl: '',
    serviceAssertions: false,
    cachedActivationId: '',
    cachedReviewStatus: 'not_requested',
  },
};

function normalizeRoleGateway(value: unknown): RoleGatewayState {
  const gateway = (value ?? {}) as Partial<RoleGatewayState>;
  const adhikara = (gateway.adhikara ?? {}) as Partial<RoleGatewayState['adhikara']>;
  const acharya = (gateway.acharya ?? {}) as Partial<RoleGatewayState['acharya']>;
  const raja = (gateway.raja ?? {}) as Partial<RoleGatewayState['raja']>;
  const legacyRaja = (gateway.raja ?? {}) as Record<string, unknown>;
  const domainPledges = (gateway.domainPledges ?? {}) as Partial<RoleGatewayState['domainPledges']>;
  const proposal = (gateway.proposal ?? {}) as Partial<RoleGatewayState['proposal']>;
  const kramaPath = (proposal.kramaPath ?? {}) as Partial<RoleGatewayState['proposal']['kramaPath']>;
  return {
    ...DEFAULT_ROLE_GATEWAY,
    selectedRole:
      gateway.selectedRole === 'shiksharthi'
      || gateway.selectedRole === 'upadhyaya'
      || gateway.selectedRole === 'acharya'
      || gateway.selectedRole === 'guru'
      || gateway.selectedRole === 'raja'
        ? gateway.selectedRole
        : '',
    city: typeof gateway.city === 'string' ? gateway.city : '',
    primaryArchetype:
      gateway.primaryArchetype === 'anveshak'
      || gateway.primaryArchetype === 'setu'
      || gateway.primaryArchetype === 'rakshak'
      || gateway.primaryArchetype === 'nirmata'
        ? gateway.primaryArchetype
        : '',
    secondaryArchetype:
      gateway.secondaryArchetype === 'anveshak'
      || gateway.secondaryArchetype === 'setu'
      || gateway.secondaryArchetype === 'rakshak'
      || gateway.secondaryArchetype === 'nirmata'
        ? gateway.secondaryArchetype
        : '',
    adhikara: {
      hridaya: Number.isFinite(Number(adhikara.hridaya)) ? Number(adhikara.hridaya) : null,
      vak: Number.isFinite(Number(adhikara.vak)) ? Number(adhikara.vak) : null,
      jnana: Number.isFinite(Number(adhikara.jnana)) ? Number(adhikara.jnana) : null,
      karma: Number.isFinite(Number(adhikara.karma)) ? Number(adhikara.karma) : null,
      phala: Number.isFinite(Number(adhikara.phala)) ? Number(adhikara.phala) : null,
    },
    acharya: {
      shravana: !!acharya.shravana,
      smarana: !!acharya.smarana,
      samvada: !!acharya.samvada,
      vinaya: !!acharya.vinaya,
      seva: !!acharya.seva,
      niyama: !!acharya.niyama,
      dambhahin: !!acharya.dambhahin,
    },
    raja: {
      dana: !!raja.dana,
      vrata: !!raja.vrata || !!legacyRaja.satya,
      brahmacharya: !!raja.brahmacharya || !!legacyRaja.nyaya,
      brahma_dharanam: !!raja.brahma_dharanam || !!legacyRaja.dharma,
      dama: !!raja.dama || !!legacyRaja.sankalpa,
      prashantata: !!raja.prashantata || !!legacyRaja.lokaSeva,
      bhuta_anukampana: !!raja.bhuta_anukampana || !!legacyRaja.samvedana,
      anrishansya: !!raja.anrishansya || !!legacyRaja.parihara,
      parasva_adana_varjanam: !!raja.parasva_adana_varjanam || !!legacyRaja.parisara,
      vyalika_karanam_tyaga: !!raja.vyalika_karanam_tyaga || !!legacyRaja.annadata,
      matapitru_shushrusha: !!raja.matapitru_shushrusha || !!legacyRaja.kala,
      devata_atithi_guru_pujanam: !!raja.devata_atithi_guru_pujanam || !!legacyRaja.vidya,
      ghrina: !!raja.ghrina || !!legacyRaja.maryada,
      nitya_indriya_sanyama: !!raja.nitya_indriya_sanyama || !!legacyRaja.sahayog,
    },
    domainPledges: {
      learning: !!domainPledges.learning,
      service: !!domainPledges.service,
      finance: !!domainPledges.finance,
      family: !!domainPledges.family,
      community: !!domainPledges.community,
    },
    proposal: {
      projectName: typeof proposal.projectName === 'string' ? proposal.projectName : '',
      projectType:
        proposal.projectType === 'homemaking'
        || proposal.projectType === 'cultural_education'
        || proposal.projectType === 'enterprise'
        || proposal.projectType === 'media'
        || proposal.projectType === 'service'
          ? proposal.projectType
          : '',
      primaryCategory: typeof proposal.primaryCategory === 'string' ? proposal.primaryCategory : '',
      secondaryCategory: typeof proposal.secondaryCategory === 'string' ? proposal.secondaryCategory : '',
      studiedProjects: Array.isArray(proposal.studiedProjects)
        ? proposal.studiedProjects.filter((value): value is string => typeof value === 'string' && value.length > 0)
        : [],
      kramaPath: {
        bija: typeof kramaPath.bija === 'string' ? kramaPath.bija : '',
        ankura: typeof kramaPath.ankura === 'string' ? kramaPath.ankura : '',
        parna: typeof kramaPath.parna === 'string' ? kramaPath.parna : '',
        pushpa: typeof kramaPath.pushpa === 'string' ? kramaPath.pushpa : '',
        moksha: typeof kramaPath.moksha === 'string' ? kramaPath.moksha : '',
      },
      societalValue: typeof proposal.societalValue === 'string' ? proposal.societalValue : '',
      personalCommitment: typeof proposal.personalCommitment === 'string' ? proposal.personalCommitment : '',
      totalEstimate: typeof proposal.totalEstimate === 'string' ? proposal.totalEstimate : '',
      duration: typeof proposal.duration === 'string' ? proposal.duration : '',
      rolesNeeded: typeof proposal.rolesNeeded === 'string' ? proposal.rolesNeeded : '',
      draftPlan: typeof proposal.draftPlan === 'string' ? proposal.draftPlan : '',
      reviewStatus:
        proposal.reviewStatus === 'requested' || proposal.reviewStatus === 'reviewed'
          ? proposal.reviewStatus
          : 'draft',
    },
    guruAttestationStatus:
      gateway.guruAttestationStatus === 'requested' || gateway.guruAttestationStatus === 'approved'
        ? gateway.guruAttestationStatus
        : 'pending',
    completedAt: typeof gateway.completedAt === 'string' ? gateway.completedAt : '',
    sevaProvider: (() => {
      const sp = (gateway.sevaProvider ?? {}) as Partial<RoleGatewayState['sevaProvider']>;
      const validStatuses = ['not_requested', 'pending', 'approved', 'rejected'] as const;
      return {
        enabled: sp.enabled === true,
        dimensions: Array.isArray(sp.dimensions)
          ? sp.dimensions.filter((d): d is string => typeof d === 'string' && d.length > 0)
          : [],
        portfolioUrl: typeof sp.portfolioUrl === 'string' ? sp.portfolioUrl : '',
        serviceAssertions: sp.serviceAssertions === true,
        cachedActivationId: typeof sp.cachedActivationId === 'string' ? sp.cachedActivationId : '',
        cachedReviewStatus: validStatuses.includes(sp.cachedReviewStatus as typeof validStatuses[number])
          ? (sp.cachedReviewStatus as RoleGatewayState['sevaProvider']['cachedReviewStatus'])
          : 'not_requested',
      };
    })(),
  };
}

function hasMeaningfulValue(value: unknown): boolean {
  if (Array.isArray(value)) return value.length > 0;
  if (typeof value === 'string') return value.trim().length > 0;
  return typeof value === 'number' ? Number.isFinite(value) : !!value;
}

function roleGatewayCompletion(profile: UserProfile): number {
  const gateway = profile.roleGateway ?? DEFAULT_ROLE_GATEWAY;
  const required: boolean[] = [
    hasMeaningfulValue(profile.alias),
    hasMeaningfulValue(profile.age),
    hasMeaningfulValue(profile.nativeLanguage),
    hasMeaningfulValue(profile.preferredScript),
    hasMeaningfulValue(profile.timeZone),
    hasMeaningfulValue(profile.learningGoals),
    hasMeaningfulValue(profile.sessionModes),
    hasMeaningfulValue(profile.lookingFor),
    hasMeaningfulValue(profile.availability),
    hasMeaningfulValue(gateway.selectedRole),
    hasMeaningfulValue(gateway.city),
    hasMeaningfulValue(gateway.adhikara.hridaya),
    hasMeaningfulValue(gateway.adhikara.vak),
    hasMeaningfulValue(gateway.adhikara.jnana),
    hasMeaningfulValue(gateway.adhikara.karma),
    hasMeaningfulValue(gateway.adhikara.phala),
  ];

  const role = gateway.selectedRole;
  if (role === 'shiksharthi') {
    required.push(
      hasMeaningfulValue(gateway.primaryArchetype),
      hasMeaningfulValue(gateway.secondaryArchetype),
    );
  }

  if (role === 'acharya' || role === 'guru' || role === 'raja') {
    required.push(
      gateway.acharya.shravana,
      gateway.acharya.smarana,
      gateway.acharya.samvada,
      gateway.acharya.vinaya,
      gateway.acharya.seva,
      gateway.acharya.niyama,
      gateway.acharya.dambhahin,
    );
  }

  if (role === 'raja') {
    required.push(
      gateway.raja.dana,
      gateway.raja.vrata,
      gateway.raja.brahmacharya,
      gateway.raja.brahma_dharanam,
      gateway.raja.dama,
      gateway.raja.prashantata,
      gateway.raja.bhuta_anukampana,
      gateway.raja.anrishansya,
      gateway.raja.parasva_adana_varjanam,
      gateway.raja.vyalika_karanam_tyaga,
      gateway.raja.matapitru_shushrusha,
      gateway.raja.devata_atithi_guru_pujanam,
      gateway.raja.ghrina,
      gateway.raja.nitya_indriya_sanyama,
      hasMeaningfulValue(gateway.domainPledges.learning),
      hasMeaningfulValue(gateway.domainPledges.service),
      hasMeaningfulValue(gateway.domainPledges.finance),
      hasMeaningfulValue(gateway.domainPledges.family),
      hasMeaningfulValue(gateway.domainPledges.community),
      hasMeaningfulValue(gateway.proposal.projectName),
      hasMeaningfulValue(gateway.proposal.projectType),
      hasMeaningfulValue(gateway.proposal.primaryCategory),
      hasMeaningfulValue(gateway.proposal.secondaryCategory),
      gateway.proposal.studiedProjects.length >= 3,
      hasMeaningfulValue(gateway.proposal.kramaPath.bija),
      hasMeaningfulValue(gateway.proposal.kramaPath.ankura),
      hasMeaningfulValue(gateway.proposal.kramaPath.parna),
      hasMeaningfulValue(gateway.proposal.kramaPath.pushpa),
      hasMeaningfulValue(gateway.proposal.kramaPath.moksha),
      hasMeaningfulValue(gateway.proposal.societalValue),
      hasMeaningfulValue(gateway.proposal.personalCommitment),
      hasMeaningfulValue(gateway.proposal.totalEstimate),
      hasMeaningfulValue(gateway.proposal.duration),
      hasMeaningfulValue(gateway.proposal.rolesNeeded),
      hasMeaningfulValue(gateway.proposal.draftPlan),
      gateway.guruAttestationStatus !== 'pending',
    );
  }

  const filled = required.filter(Boolean).length;
  return Math.round((filled / required.length) * 100);
}

export function computeCompletion(profile: UserProfile): number {
  return roleGatewayCompletion(profile);
}

/** Deterministic hue from UID for alias-avatar colour (no PII required). */
export function uidToHue(uid: string): number {
  let hash = 0;
  for (let i = 0; i < uid.length; i++) hash = (hash * 31 + uid.charCodeAt(i)) >>> 0;
  return hash % 360;
}

/** Extract the non-PII public subset of a profile to send to the server. */
export function buildPublicProfile(p: UserProfile): PublicProfile {
  const age = Number.parseInt(p.age, 10);
  const rating = Number.parseFloat(p.rating);
  return {
    uid: p.uid,
    alias: p.alias,
    profileKind: p.profileKind,
    age: Number.isFinite(age) ? age : undefined,
    rating: Number.isFinite(rating) ? rating : undefined,
    learningLevel: p.learningLevel,
    nativeLanguage: p.nativeLanguage,
    preferredScript: p.preferredScript,
    timeZone: p.timeZone,
    pincode: p.pincode,
    studyAreas: splitStudyAreas(p.studyAreas),
    qualification: p.qualification,
    sessionModes: p.sessionModes,
    availability: p.availability,
    collaborationMode: p.collaborationMode,
    lookingFor: p.lookingFor,
    gatewayRole: p.roleGateway?.selectedRole || undefined,
    activeRole: p.activeRole || undefined,
  };
}

// ── localStorage helpers ────────────────────────────────────────────────────

const profileKey     = (uid: string) => `garuda_profile_${uid}`;
const connectionsKey = (uid: string) => `garuda_connections_${uid}`;

function loadProfile(uid: string): UserProfile | null {
  try {
    const raw = localStorage.getItem(profileKey(uid));
    return raw ? (JSON.parse(raw) as UserProfile) : null;
  } catch {
    return null;
  }
}

function persistProfile(profile: UserProfile): void {
  try {
    localStorage.setItem(profileKey(profile.uid), JSON.stringify(profile));
  } catch {
    // Safari private mode / quota — keep session in memory only
  }
}

function loadConnections(uid: string): Connection[] {
  try {
    const raw = localStorage.getItem(connectionsKey(uid));
    return raw ? (JSON.parse(raw) as Connection[]) : [];
  } catch {
    return [];
  }
}

function persistConnections(uid: string, list: Connection[]): void {
  try {
    localStorage.setItem(connectionsKey(uid), JSON.stringify(list));
  } catch {
    // Safari private mode / quota
  }
}

function splitStudyAreas(value: string): string[] {
  return value
    .split(',')
    .map(part => part.trim())
    .filter(Boolean);
}

function normalizeStudyAreas(value: unknown): string {
  if (Array.isArray(value)) {
    return value.map(item => String(item).trim()).filter(Boolean).join(', ');
  }
  if (typeof value === 'string') return value;
  return '';
}

function normalizeAge(value: unknown): string {
  return String(value ?? '').replace(/[^0-9]/g, '').slice(0, 3);
}

function normalizeRating(value: unknown): string {
  const cleaned = String(value ?? '').replace(/[^0-9.]/g, '');
  if (!cleaned) return '';
  const [whole, decimal = ''] = cleaned.split('.');
  return decimal ? `${whole.slice(0, 1)}.${decimal.slice(0, 1)}` : whole.slice(0, 1);
}

function normalizeProfileKind(value: unknown): ProfileKind {
  if (value === 'learner' || value === 'knowledge_provider' || value === 'both') {
    return value;
  }
  return 'learner';
}

function normalizeSimpleRole(value: unknown): 'Shiksharthi / Learner' | 'Educator' | 'Service Provider' | 'Service Consumer' | undefined {
  if (value === 'Shiksharthi / Learner' || value === 'Educator' || value === 'Service Provider' || value === 'Service Consumer') {
    return value as any;
  }
  // Backward compatibility for previously stored values (from earlier simplifications)
  if (value === 'Student' || value === 'शिक्षार्थी' || value === 'shiksharthi') return 'Shiksharthi / Learner';
  if (value === 'Producer') return 'Service Provider';
  return undefined;
}

function normalizeSessionModes(value: unknown): SessionMode[] {
  const valid: SessionMode[] = [];
  const raw = Array.isArray(value)
    ? value
    : typeof value === 'string'
      ? value.split(',')
      : [];
  for (const item of raw) {
    const mode = String(item).trim().toLowerCase();
    if (mode === 'online' || mode === 'offline') valid.push(mode);
  }
  return Array.from(new Set(valid));
}

function normalizeAvailability(value: unknown): UserProfile['availability'] {
  const valid: UserProfile['availability'] = [];
  const raw = Array.isArray(value)
    ? value
    : typeof value === 'string'
      ? value.split(',')
      : [];
  for (const item of raw) {
    const slot = String(item).trim().toLowerCase();
    if (slot === 'morning' || slot === 'afternoon' || slot === 'evening' || slot === 'weekends') {
      valid.push(slot);
    }
  }
  return Array.from(new Set(valid));
}

/** Ensures the roles array always contains FOUNDATION_ROLE and only valid RoleNames. */
function normalizeRoles(value: unknown): RoleName[] {
  if (!Array.isArray(value)) return [FOUNDATION_ROLE];
  const valid = value
    .filter((r): r is RoleName => ROLE_OPTIONS.includes(r as RoleName))
    .filter(r => r !== FOUNDATION_ROLE);
  return [FOUNDATION_ROLE, ...valid];
}

function normalizeProfile(profile: Partial<UserProfile> & { uid: string }, fallbackTimeZone?: string): UserProfile {
  return {
    uid: profile.uid,
    alias: profile.alias ?? '',
    profileKind: normalizeProfileKind(profile.profileKind),
    age: normalizeAge(profile.age),
    rating: normalizeRating(profile.rating),
    learningLevel: profile.learningLevel ?? 'beginner',
    learningGoals: profile.learningGoals ?? '',
    nativeLanguage: profile.nativeLanguage ?? '',
    preferredScript: profile.preferredScript ?? 'devanagari',
    activeRole: ROLE_OPTIONS.includes(profile.activeRole as RoleName) ? (profile.activeRole as RoleName) : FOUNDATION_ROLE,
    roles: normalizeRoles(profile.roles),
    roleActivity: normalizeRoleActivity(profile.roleActivity),
    timeZone: profile.timeZone ?? fallbackTimeZone ?? Intl.DateTimeFormat().resolvedOptions().timeZone,
    pincode: profile.pincode ?? '',
    // New core PII
    dob: profile.dob ?? '',
    cell: profile.cell ?? '',
    emailContact: profile.emailContact ?? '',
    role: normalizeSimpleRole(profile.role),
    studyAreas: normalizeStudyAreas(profile.studyAreas),
    qualification: profile.qualification ?? '',
    sessionModes: normalizeSessionModes(profile.sessionModes),
    acharyaAcknowledgedAt: profile.acharyaAcknowledgedAt ?? '',
    acharyaAcknowledgedVersion: profile.acharyaAcknowledgedVersion ?? '',
    roleGateway: normalizeRoleGateway(profile.roleGateway),
    isDiscoverable: profile.isDiscoverable ?? false,
    availability: normalizeAvailability(profile.availability),
    collaborationMode: profile.collaborationMode ?? 'either',
    lookingFor: profile.lookingFor ?? 'peer_group',
    federatedIdentity: normalizeFederatedIdentity(profile.federatedIdentity),
    createdAt: profile.createdAt ?? new Date().toISOString(),
    updatedAt: profile.updatedAt ?? new Date().toISOString(),
    profileVisibility: profile.profileVisibility ?? { ...DEFAULT_VISIBILITY },
    allowedEmails:     Array.isArray(profile.allowedEmails)  ? profile.allowedEmails  : [],
    allowedAliases:    Array.isArray(profile.allowedAliases) ? profile.allowedAliases : [],
  };
}

function normalizeFederatedIdentity(value: unknown): FederatedIdentitySnapshot | undefined {
  if (!value || typeof value !== 'object') return undefined;
  const identity = value as Partial<FederatedIdentitySnapshot>;
  const providerIds = Array.isArray(identity.providerIds)
    ? identity.providerIds.filter((item): item is string => typeof item === 'string' && item.length > 0)
    : [];
  if (!identity.displayName && !identity.email && !identity.phoneNumber && !identity.photoURL && providerIds.length === 0) {
    return undefined;
  }
  return {
    displayName: typeof identity.displayName === 'string' ? identity.displayName : '',
    email: typeof identity.email === 'string' ? identity.email : '',
    phoneNumber: typeof identity.phoneNumber === 'string' ? identity.phoneNumber : '',
    photoURL: typeof identity.photoURL === 'string' ? identity.photoURL : '',
    providerIds,
    lastSyncedAt: typeof identity.lastSyncedAt === 'string' ? identity.lastSyncedAt : new Date().toISOString(),
  };
}

function extractFederatedIdentity(user: User): FederatedIdentitySnapshot {
  const providers = user.providerData
    .map((provider) => provider.providerId)
    .filter((value): value is string => typeof value === 'string' && value.length > 0);
  return {
    displayName: user.displayName ?? '',
    email: user.email ?? '',
    phoneNumber: user.phoneNumber ?? '',
    photoURL: user.photoURL ?? '',
    providerIds: Array.from(new Set(providers)),
    lastSyncedAt: new Date().toISOString(),
  };
}

/** Default profile: only the uid and safe defaults — no PII from Firebase User. */
function makeDefaultProfile(user: User): UserProfile {
  return normalizeProfile({
    uid: user.uid,
    activeRole: FOUNDATION_ROLE,
    roles: [FOUNDATION_ROLE],
    roleActivity: DEFAULT_ROLE_ACTIVITY,
    timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  });
}

// ── Context ─────────────────────────────────────────────────────────────────

interface AuthContextType {
  user: User | null;
  profile: UserProfile | null;
  policy: PlatformPolicyConfig | null;
  authLoading: boolean;
  isAdmin: boolean;
  adminLoading: boolean;
  connections: Connection[];
  signInWithGoogle: () => Promise<void>;
  signInWithX: () => Promise<void>;
  sendEmailSignInLink: (email: string) => Promise<void>;
  completeEmailSignInLink: (email: string) => Promise<void>;
  emailLinkPrompt: boolean;
  signOut: () => Promise<void>;
  deleteAccount: () => Promise<void>;
  updateProfile: (updates: Partial<UserProfile>) => void;
  exportProfile: () => void;
  importProfile: (file: File) => Promise<void>;
  // Discovery
  syncDiscovery: (profileOverride?: UserProfile) => Promise<void>;
  removeFromDiscovery: (profileOverride?: UserProfile) => Promise<void>;
  generateInvite: () => Promise<string>;
  redeemInvite: (code: string) => Promise<InviteRedeemResult>;
  addConnection: (p: PublicProfile) => void;
  removeConnection: (uid: string) => void;
}

const AuthContext = createContext<AuthContextType | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [policy, setPolicy] = useState<PlatformPolicyConfig | null>(null);
  const [connections, setConnections] = useState<Connection[]>([]);
  const [authLoading, setAuthLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [adminLoading, setAdminLoading] = useState(false);
  const [emailLinkPrompt, setEmailLinkPrompt] = useState(false);

  useEffect(() => {
    let cancelled = false;
    getPortalConfig('en')
      .then((config) => {
        if (!cancelled) setPolicy(config.policy ?? null);
      })
      .catch((error) => {
        console.error('Failed to load policy config:', error);
        if (!cancelled) setPolicy(null);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const emailLinkSettings = {
    url: `${window.location.origin}${window.location.pathname}`,
    handleCodeInApp: true,
  };

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (u) => {
      setUser(u);
      if (u) {
        const stored = loadProfile(u.uid);
        const p = normalizeProfile(stored ?? makeDefaultProfile(u), Intl.DateTimeFormat().resolvedOptions().timeZone);
        const withIdentity = normalizeProfile({
          ...p,
          federatedIdentity: extractFederatedIdentity(u),
        }, Intl.DateTimeFormat().resolvedOptions().timeZone);
        persistProfile(withIdentity);
        setProfile(withIdentity);
        setConnections(loadConnections(u.uid));
        // Check admin status — never cached, always fresh on auth state change
        setAdminLoading(true);
        u.getIdToken()
          .then((token) =>
            fetch('/api/admin/me', { headers: { Authorization: `Bearer ${token}` } }),
          )
          .then((r) => r.json())
          .then((data) => setIsAdmin(data.isAdmin === true))
          .catch(() => setIsAdmin(false))
          .finally(() => setAdminLoading(false));
      } else {
        setProfile(null);
        setConnections([]);
        setIsAdmin(false);
        setAdminLoading(false);
      }
      setAuthLoading(false);
    });
    return unsub;
  }, []);

  useEffect(() => {
    if (!profile || !policy) return;
    emitGatewayMetrics(profile, policy, 'profile-state');
  }, [profile, policy]);

  useEffect(() => {
    if (authLoading || user) return;
    if (!isSignInWithEmailLink(auth, window.location.href)) return;

    let storedEmail: string | null = null;
    try {
      storedEmail = window.localStorage.getItem('emailForSignIn');
    } catch {
      storedEmail = null;
    }
    if (storedEmail) {
      signInWithEmailLink(auth, storedEmail, window.location.href)
        .then(() => {
          try { window.localStorage.removeItem('emailForSignIn'); } catch { /* private mode */ }
          setEmailLinkPrompt(false);
          window.history.replaceState({}, '', window.location.pathname);
        })
        .catch((error) => {
          console.error('Email link completion failed:', error);
          setEmailLinkPrompt(true);
        });
      return;
    }

    setEmailLinkPrompt(true);
  }, [authLoading, user]);

  const signInWithGoogle = async () => {
    await signInWithPopup(auth, googleProvider);
  };

  const signInWithX = async () => {
    await signInWithPopup(auth, twitterProvider);
  };

  const sendEmailSignInLink = async (email: string) => {
    await sendSignInLinkToEmail(auth, email, emailLinkSettings);
    try {
      window.localStorage.setItem('emailForSignIn', email);
    } catch {
      // Safari private mode
    }
    setEmailLinkPrompt(true);
  };

  const completeEmailSignInLink = async (email: string) => {
    await signInWithEmailLink(auth, email, window.location.href);
    window.localStorage.removeItem('emailForSignIn');
    setEmailLinkPrompt(false);
    window.history.replaceState({}, '', window.location.pathname);
  };

  const signOut = async () => {
    await firebaseSignOut(auth);
  };

  /** Full erasure — satisfies DPDP Act 2023 S.12 right to erasure. */
  const deleteAccount = async () => {
    if (!user || !profile) return;
    // 1. Remove from server-side discovery pool (best-effort)
    try { await removePublicProfile(profile.uid); } catch { /* ignore */ }
    // 2. Wipe all localStorage data for this user
    localStorage.removeItem(profileKey(profile.uid));
    localStorage.removeItem(connectionsKey(profile.uid));
    // 3. Wipe consent record
    clearConsentRecord();
    // 4. Delete the Firebase Auth account (irreversible)
    await deleteUser(user);
    // onAuthStateChanged fires → clears user/profile/connections state
  };

  const updateProfile = (updates: Partial<UserProfile>) => {
    if (!profile) return;
    const updated = normalizeProfile({
      ...profile,
      ...updates,
      updatedAt: new Date().toISOString(),
    });
    persistProfile(updated);
    setProfile(updated);
  };

  const exportProfile = () => {
    if (!profile) return;
    const { federatedIdentity, ...portableProfile } = profile;
    const blob = new Blob([JSON.stringify(portableProfile, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `garuda-profile-${profile.uid.slice(0, 8)}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const importProfile = async (file: File): Promise<void> => {
    const text = await file.text();
    const parsed = JSON.parse(text) as Partial<UserProfile> & { uid?: string };
    if (!parsed.uid) throw new Error('Invalid profile file — missing uid');
    const { federatedIdentity, ...portableProfile } = parsed;
    const imported = normalizeProfile(portableProfile as Partial<UserProfile> & { uid: string });
    persistProfile(imported);
    setProfile(imported);
  };

  const syncDiscovery = async (profileOverride?: UserProfile) => {
    const source = profileOverride ?? profile;
    if (!source) return;
    const idToken = user ? await user.getIdToken() : undefined;
    await syncPublicProfile(
      source.uid,
      buildPublicProfile(source),
      {
        settings:       source.profileVisibility,
        allowedEmails:  source.allowedEmails,
        allowedAliases: source.allowedAliases,
      },
      idToken,
    );
  };

  const removeFromDiscovery = async (profileOverride?: UserProfile) => {
    const source = profileOverride ?? profile;
    if (!source) return;
    const idToken = user ? await user.getIdToken() : undefined;
    await removePublicProfile(source.uid, idToken);
  };

  const generateInvite = async (): Promise<string> => {
    if (!profile) throw new Error('Not signed in');
    return createInvite(profile.uid, buildPublicProfile(profile));
  };

  const redeemInvite = (code: string) => redeemInviteCode(code);

  const addConnection = (p: PublicProfile) => {
    if (!user) return;
    const conn: Connection = {
      uid: p.uid,
      alias: p.alias,
      profileKind: p.profileKind as ProfileKind | undefined,
      age: p.age !== undefined ? String(p.age) : undefined,
      rating: p.rating !== undefined ? String(p.rating) : undefined,
      learningLevel: p.learningLevel,
      nativeLanguage: p.nativeLanguage,
      timeZone: p.timeZone,
      pincode: p.pincode,
      studyAreas: p.studyAreas,
      qualification: p.qualification,
      sessionModes: p.sessionModes,
      lookingFor: p.lookingFor,
      connectedAt: new Date().toISOString(),
    };
    const updated = [conn, ...connections.filter(c => c.uid !== p.uid)];
    persistConnections(user.uid, updated);
    setConnections(updated);
  };

  const removeConnection = (uid: string) => {
    if (!user) return;
    const updated = connections.filter(c => c.uid !== uid);
    persistConnections(user.uid, updated);
    setConnections(updated);
  };

  return (
    <AuthContext.Provider value={{
      user, profile, policy, authLoading, isAdmin, adminLoading, connections,
      signInWithGoogle, signInWithX, signOut, deleteAccount,
      sendEmailSignInLink, completeEmailSignInLink, emailLinkPrompt,
      updateProfile, exportProfile, importProfile,
      syncDiscovery, removeFromDiscovery,
      generateInvite, redeemInvite,
      addConnection, removeConnection,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextType {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
}
